import React from 'react';
import { PlaceDetails, getOpenStatus } from './placeDetails';
import './MapPlaceSidebar.css';

interface MapPlaceOverlayProps {
  place: PlaceDetails | null;
  isLoading: boolean;
  fallbackTitle?: string;
  onClose: () => void;
  /**
   * Escape closes this card rather than whatever is behind it. On in the full
   * map, whose own Escape handler would otherwise close the whole map.
   */
  closeOnEscape?: boolean;
}

const getTodayHours = (place: PlaceDetails): string | null => {
  const weekdayText = place.opening_hours?.weekday_text;
  if (!weekdayText?.length) return null;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayRow = weekdayText.find((row) => row.toLowerCase().startsWith(today.toLowerCase()));
  return todayRow ?? weekdayText[0];
};

const MAX_THUMB_PREVIEWS = 6;

const MapPlaceOverlay: React.FC<MapPlaceOverlayProps> = ({
  place,
  isLoading,
  fallbackTitle,
  onClose,
  closeOnEscape = false,
}) => {
  const title = place?.name || fallbackTitle || 'Selected place';

  React.useEffect(() => {
    if (!closeOnEscape) return undefined;
    // Capture phase on window runs before any bubbling Escape handler, and
    // stopping it there keeps them from running at all.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [closeOnEscape, onClose]);
  const openStatus = place ? getOpenStatus(place) : null;
  const todayHours = place ? getTodayHours(place) : null;
  const photos = place?.photos?.slice(0, MAX_THUMB_PREVIEWS) ?? [];
  const [primaryIndex, setPrimaryIndex] = React.useState(0);

  React.useEffect(() => {
    setPrimaryIndex(0);
  }, [place?.name]);

  const primaryPhoto = photos[primaryIndex];

  const handleSelectPhoto = (index: number) => {
    setPrimaryIndex(index);
  };

  return (
    <section className="v2-map-place-overlay" aria-label="Location preview">
      <button
        type="button"
        className="v2-map-place-overlay__close"
        onClick={onClose}
        aria-label="Close location preview"
      >
        ×
      </button>

      {isLoading ? (
        <div className="v2-map-place-overlay__loading">
          <span className="v2-place-card__spinner" aria-hidden />
          <p>Loading place details...</p>
        </div>
      ) : (
        <div className="v2-map-place-overlay__body">
          <div className="v2-map-place-overlay__top">
            <div className="v2-map-place-overlay__content">
              <h3>{title}</h3>

              {place?.rating !== undefined && (
                <div className="v2-map-place-overlay__rating">
                  <span className="v2-place-card__stars" aria-hidden>
                    {'★'.repeat(Math.round(place.rating))}
                    {'☆'.repeat(Math.max(0, 5 - Math.round(place.rating)))}
                  </span>
                  <strong>{place.rating.toFixed(1)}</strong>
                  {place.user_ratings_total ? (
                    <span>({place.user_ratings_total.toLocaleString()} reviews)</span>
                  ) : null}
                </div>
              )}

              {place?.formatted_address && (
                <p className="v2-map-place-overlay__address">{place.formatted_address}</p>
              )}

              {place && (todayHours || openStatus !== null) && (
                <div className="v2-map-place-overlay__hours">
                  {openStatus !== null && (
                    <span
                      className={`v2-map-place-overlay__hours-status ${
                        openStatus
                          ? 'v2-map-place-overlay__hours-status--open'
                          : 'v2-map-place-overlay__hours-status--closed'
                      }`}
                    >
                      {openStatus ? 'Open now' : 'Closed now'}
                    </span>
                  )}
                  {todayHours && <p>{todayHours}</p>}
                </div>
              )}
            </div>

            {primaryPhoto && (
              <img
                src={primaryPhoto.getUrl({ maxWidth: 480, maxHeight: 360 })}
                alt=""
                className="v2-map-place-overlay__photo--primary"
              />
            )}
          </div>

          {photos.length > 0 && (
            <div className="v2-map-place-overlay__thumbs" aria-label="Place photo previews">
              {photos.map((photo, index) => (
                <button
                  type="button"
                  key={`${title}-thumb-${index}`}
                  className={`v2-map-place-overlay__thumb-btn ${
                    index === primaryIndex ? 'v2-map-place-overlay__thumb-btn--active' : ''
                  }`}
                  onClick={() => handleSelectPhoto(index)}
                  aria-label={`Show photo ${index + 1} as main image`}
                  aria-pressed={index === primaryIndex}
                >
                  <img
                    src={photo.getUrl({ maxWidth: 200, maxHeight: 120 })}
                    alt=""
                    className="v2-map-place-overlay__photo--thumb"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default MapPlaceOverlay;
